
import Vue from 'vue';
import {
  SmallDetailsLoader,
  SmallDetailsMultipleLogos,
} from '@/components/SmallDetails';
import { revalidateSmallDetails } from '@/utils/smallDetailsHelperV2';
import SmallDetailsConfirmation from '@/components/SmallDetails/Confirmation/SmallDetailsConfirmation.vue';
import { mapState, mapStores, mapActions } from 'pinia';
import { useStore as useShapeStore } from '@/stores/v2/shape';
import { useStore as useProductStore } from '@/stores/v2/product';
import { useStore as useNotificationStore } from '@/stores/v2/notification';
import { useStore as useGlobalStore } from '@/stores/v2/global';
import { useStore as useLangStore } from '@/stores/v2/lang';
import { useStore as useHistoryStore } from '@/stores/v2/history';
import Reset16 from '@carbon/icons-vue/es/reset/16';
import { Personalization } from '@midocean/webapps-shared-domain';
import {
  isRevalidationNeeded,
  trySavePersonalization,
} from '@/utils/personalizationHelperV2';
import { PdpChoicesSetterType } from '@/setters/typings';
import i18n from '@/i18n';
import { createStateSnapshot } from '@/utils/historyHelperV2';
import { SmallDetailsResult } from '@/typings';
import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import SmallDetailsSingleLogoModal from '../SmallDetailsSingleLogo/SmallDetailsSingleLogoModal.vue';
import { useStore as useProductStoreV2 } from '@/stores/v2/product';
import { lineWidth } from '@/constants/areaLineWidth';
import { px2mm } from '@/utils';
import customersAPI from '@/services/CustomersService';
import { NewLogoImprovementRequestForm } from '@/typings/SmallDetails';
import { LOGO_IMPROVEMENT_FLOW_ACTIVE } from '@/constants/smallDetails';
import LogoImprovementRequestSuccessModal from '../LogoImprovementRequestSuccessModal/LogoImprovementRequestSuccessModal.vue';
import { CvLoading } from '@carbon/vue';
import { getIntershopRedirectionUrl } from '@/utils/index';

declare module 'vue/types/vue' {
  interface Vue {
    $pdpChoices: PdpChoicesSetterType;
    $sapCustomerId: string;
    $isEditMode: boolean;
    $personalization: Personalization;
    $isDirectApproval: boolean;
    $customerCompanyLogoUrl: string;
    $userLoginOptions: { data: string; iv: string };
    $context: string;
    $isCommunicationSentToCC: boolean;
  }
}

export default Vue.extend({
  name: 'ManualCheck',
  components: {
    SmallDetailsLoader,
    SmallDetailsSingleLogoModal,
    SmallDetailsConfirmation,
    Reset16,
    SmallDetailsMultipleLogos,
    LogoImprovementRequestSuccessModal,
    CvLoading,
  },
  inject: [
    '$pdpChoices',
    '$sapCustomerId',
    '$isEditMode',
    '$personalization',
    '$isDirectApproval',
    '$customerCompanyLogoUrl',
    '$userLoginOptions',
    '$context',
    '$isCommunicationSentToCC',
  ],
  props: {
    sapCustomerId: {
      type: String,
      required: true,
    },
    triggerSmallDetailsCheck: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConcept: {
      type: Boolean,
      required: false,
      default: false,
    },
    sapCustomerEmail: {
      type: String,
      required: false,
      default: '',
    },
    region: {
      type: String,
      required: false,
      default: '',
    },
  },
  data() {
    return {
      isLoaderOpen: false,
      isSmallDetailsOpen: false,
      isConfirmationOpen: false,
      smallDetailsResult: {} as SmallDetailsResult | null,
      activeShape: {} as AnnotationShape | undefined,
      isMultipleSmallDetailsOpen: false,
      LOGO_IMPROVEMENT_FLOW_ACTIVE,
      isCreatingLogoImprovementRequest: false,
      getIntershopRedirectionUrl,
    };
  },
  computed: {
    ...mapStores(useShapeStore, useProductStore, useNotificationStore),
    ...mapState(useShapeStore, ['shapes', 'positionShapeMap', 'activeShapeId']),
    ...mapState(useProductStore, ['data', 'unavailablePrintPositions']),
    ...mapState(useLangStore, ['languageInUse']),
    ...mapState(useNotificationStore, ['notifications']),

    fileName(): string {
      if (this.activeShape && this.activeShape.kind === 'Logo') {
        return this.activeShape.fileName || '';
      } else if (this.shapes.length >= 1 && this.shapes[0].kind === 'Logo') {
        return this.shapes[0].fileName || '';
      }
      return '';
    },

    pngFile(): string | Blob {
      if (this.activeShape && this.activeShape.kind === 'Logo') {
        return this.activeShape.pngFile || '';
      } else if (this.shapes.length >= 1 && this.shapes[0].kind === 'Logo') {
        return this.shapes[0].pngFile || '';
      }
      return '';
    },

    isDisabled(): boolean {
      return (
        this.shapes.filter((x) => x.smallDetailsResult !== undefined).length ===
        0
      );
    },
    checkStatusLabel(): string {
      let worstStatus = '';
      let worstStatusValue = 3;
      this.shapes.forEach((x) => {
        if (x.smallDetailsResult) {
          if (!x.smallDetailsResult.isCheckValid) {
            worstStatus = i18n
              .t(`wizard-v2.small-details.invalid-check`)
              .toString()
              .toUpperCase();
            worstStatusValue = 0;
          }
          if (
            x.smallDetailsResult.isCheckValid &&
            x.smallDetailsResult.status === 'Bad' &&
            worstStatusValue > 0
          ) {
            worstStatus = i18n
              .t(
                `wizard-v2.small-details.${x?.smallDetailsResult.status.toLowerCase()}`
              )
              .toString()
              .toUpperCase();
            worstStatusValue = 1;
          }
          if (
            x.smallDetailsResult.isCheckValid &&
            x.smallDetailsResult.status === 'Average' &&
            worstStatusValue > 1
          ) {
            worstStatus = i18n
              .t(
                `wizard-v2.small-details.${x?.smallDetailsResult.status.toLowerCase()}`
              )
              .toString()
              .toUpperCase();
            worstStatusValue = 2;
          }
          if (
            x.smallDetailsResult.isCheckValid &&
            x.smallDetailsResult.status === 'Good' &&
            worstStatusValue > 2
          ) {
            worstStatus = i18n
              .t(
                `wizard-v2.small-details.${x?.smallDetailsResult.status.toLowerCase()}`
              )
              .toString()
              .toUpperCase();
            worstStatusValue = 3;
          }
        }
      });

      return worstStatus;
    },

    relevantShapes(): AnnotationShape[] {
      const store = useShapeStore();

      return store.shapes.filter((x) => x.smallDetailsResult !== undefined);
    },
  },
  watch: {
    triggerSmallDetailsCheck(newValue) {
      if (newValue) {
        this.onManualCheck();
      }
    },
  },
  methods: {
    ...mapActions(useGlobalStore, [
      'setIsProcessingOrder',
      'setIsLoaderActive',
    ]),
    ...mapActions(useNotificationStore, ['runValidators']),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useShapeStore, ['removeShape']),

    checkSmallDetails() {
      this.isConfirmationOpen = false;
      this.onManualCheck();
    },

    backToWizard() {
      this.$emit('action:reset-flag');
      this.isMultipleSmallDetailsOpen = false;
      this.isConfirmationOpen = false;
    },

    async onManualCheck() {
      this.$modal.hide('pdf-preview-modal');
      this.smallDetailsResult = null;

      if (isRevalidationNeeded()) {
        this.isLoaderOpen = true;
        await revalidateSmallDetails();
        this.runValidators();
        this.isLoaderOpen = false;

        const snapshot = createStateSnapshot();
        this.recordHistory(snapshot);
      }

      this.activeShape =
        this.shapes.find((x) => x.id === this.activeShapeId) ||
        this.shapes.find((x) => x.smallDetailsResult?.status !== 'Good');
      if (
        this.activeShape !== undefined &&
        this.activeShape.smallDetailsResult !== undefined
      ) {
        this.smallDetailsResult = this.activeShape.smallDetailsResult;
      }

      const isConfirmationNeeded =
        this.shapes.filter((x) => x.smallDetailsResult?.status !== 'Good')
          .length > 0;

      if (this.triggerSmallDetailsCheck && isConfirmationNeeded) {
        this.isConfirmationOpen = true;
      } else if (this.triggerSmallDetailsCheck && !isConfirmationNeeded) {
        this.trySavePersonalization();
      } else {
        if (this.shapes.length > 1 || !LOGO_IMPROVEMENT_FLOW_ACTIVE) {
          this.isMultipleSmallDetailsOpen = true;
          this.isSmallDetailsOpen = false;
          return;
        } else if (
          this.shapes.length === 1 &&
          this.shapes[0].smallDetailsResult != null
        ) {
          this.isSmallDetailsOpen = true;
          this.smallDetailsResult = this.shapes[0].smallDetailsResult;
        }
      }
    },

    async onRequestLogoImprovement(formData: {
      comment: string;
      improvementOption: string;
      improvementOptionKey: string;
    }) {
      this.isSmallDetailsOpen = false;
      this.isCreatingLogoImprovementRequest = true;

      if (this.activeShape && this.activeShape.kind === 'Logo') {
        const productStoreV2 = useProductStoreV2();

        const position = productStoreV2.activePosition;

        const currentPosition = productStoreV2.printPositions.find(
          (pos) => pos.name === position.name
        );

        const newLogoImprovementForm = {
          customerNumber: this.sapCustomerId,
          productSapCode: productStoreV2.data.name,
          email: this.sapCustomerEmail,
          region: this.region,
          language: this.languageInUse,
          logos: [
            {
              originalFileName: this.activeShape?.fileName,
              title: this.activeShape?.title,
              printTechniqueSapCode:
                currentPosition?.printTechniques[0].printTechnique.code,
              printPositionSapCode: position.name,
              printSizeWidthMm: this.activeShape.widthOnProductMM,
              printSizeHeightMm: this.activeShape.heightOnProductMM,
              improvementOption: formData.improvementOption,
              comment: formData.comment,
              improvementOptionKey: formData.improvementOptionKey,
            },
          ],
        } as NewLogoImprovementRequestForm;
        if (this.activeShape?.pngFile) {
          const response = await fetch(this.activeShape.originalFileUrl);
          const blob = await response.blob();
          let file = new File([blob], this.activeShape?.fileName, {
            type: blob.type,
            lastModified: Date.now(),
          });

          customersAPI.submitLogoImprovementRequest(
            [file],
            newLogoImprovementForm
          );
          this.isCreatingLogoImprovementRequest = false;
          this.$modal.show('logo-improvement-request-success-modal');
        } else {
          this.isCreatingLogoImprovementRequest = false;
        }
      }
    },

    onBackToLibrary() {
      this.isSmallDetailsOpen = false;
      this.smallDetailsResult = null;
      this.$modal.hide('small-details-single-logo-modal');
    },

    onUploadNewLogo() {
      this.removeShape(this.activeShapeId || this.shapes[0].id);
      this.isSmallDetailsOpen = false;
      this.smallDetailsResult = null;
      this.$modal.hide('small-details-single-logo-modal');
      this.$emit('modal:open');
    },

    onUseCurrentDesign() {
      const unAcceptedShapes = this.shapes.filter(
        (x) => x.smallDetailsResult?.status !== 'Good'
      );

      const utcDate = new Date().toISOString();

      unAcceptedShapes.map((x) => {
        if (x.smallDetailsResult) {
          x.smallDetailsResult.approvedAt = utcDate;
        }
      });

      this.trySavePersonalization();

      this.isConfirmationOpen = false;
    },
    async trySavePersonalization() {
      try {
        this.setIsProcessingOrder(true);
        this.setIsLoaderActive(true);
        const personalizationId = await trySavePersonalization(
          this.$isEditMode,
          this.$pdpChoices.productVariants,
          this.$sapCustomerId,
          this.isConcept,
          this.$isDirectApproval,
          this.$customerCompanyLogoUrl,
          this.languageInUse,
          this.$isCommunicationSentToCC
        );
        const eventName = this.isConcept
          ? 'wizard:saved-as-concept'
          : 'wizard:ordered';

        if (this.$context === 'CustomerCare') {
          const url = this.getIntershopRedirectionUrl(
            personalizationId,
            this.isConcept,
            this.$userLoginOptions.data,
            this.$userLoginOptions.iv
          );
          window.open(url, '_blank');
        }

        this.$emit(eventName, { personalizationId });
        this.$emit('action:disable-pintura');
      } catch (e: unknown) {
        console.log((e as Error).message);
      } finally {
        this.setIsProcessingOrder(false);
        this.setIsLoaderActive(false);
      }
    },
  },
});
